/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Switch checkbox payload
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import type { ValueOf } from '@/main/types/globalTypes';
import type { IPersonalAccount } from '@/main/types/personalAccountTypes';
import type { ISubscriberFixed } from '@/main/types/subscriberFixedTypes';
import type { ISubscriberMobile } from '@/main/types/subscriberTypes';
import { EGTMWidgetPersonalAccountCloseType } from '@modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetPersonalAccountOperations';
import type { IAction, IActionWithOptionalPayload, IActionWithPayload } from '@modules/app/createAction';
import type {
    TAutoReportIdTableAccountData,
    TAutoReportIdTableSubscriberData,
} from '@pages/PageAutoReportId/components/Table/AutoReportIdTableCell';
import type { IBills, IBillsTableFooter } from '@pages/PageBills/pageBillsTypes';
import type {
    TCampaignInfoTableAccountData,
    TCampaignInfoTableSubscriberData,
} from '@pages/PageCampaignInfo/components/Table/CampaignInfoTableCells';
import type { ICashbackHistoryTableData } from '@pages/PageCashback/types/cashbackHistoryTypes';
import type {
    IExpensesAnalyzerData,
    IExpensesAnalyzerTableFooter,
} from '@pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import type { ILabelsImportTableData } from '@pages/PageLabelsImport/labelsImportTypes';
import type { TPagePaymentsTableData } from '@pages/PagePayments/const/PagePaymentsTable';
import type { ISettingsAutoReportsTableData } from '@pages/PageSettings/components/SettingsAutoReports/components/SettingsAutoReportsTable/settingsAutoReportsTableTypes';
import type { ISettingsStatisticsTableData } from '@pages/PageSettings/components/SettingsStatistics/components/SettingsStatisticsTable/settingsStatisticsTableTypes';
import type { IWizardProductOfferingsTableData } from '@pages/WizardProductOfferings/wizardProductOfferingsTypes';
import type { IRouteParams } from '@routes/constants/routesConstants';

import type { IFilterSort, ISortEnum } from '../components/Filters/Filter';
import type { IFilterRangeParams } from '../components/Filters/FilterRange';
import type { IBasketGTMProps } from '../components/footerModal/Basket/basketTypes';
import { ETopicType } from '../components/footerModal/CreateInquiry/types/createInquiryTypes';
import type { TLabelEditorUpdatePayload } from '../components/footerModal/LabelEditor/labelEditorTypes';
import { EFastBasketSwitchboxContext } from '../constants';
import {
    ADD_TABLE_SUB_ROW_ID,
    CHANGE_TABLE_MODAL_VALUE,
    COLLAPSE_TABLE_SUB_ROWS,
    DROP_TABLE_FILTER_AND_SORT,
    GET_BASKET_COUNT,
    GET_BASKET_COUNT_FAILED,
    GET_BASKET_COUNT_SUCCESSFUL,
    LOAD_MOBILE_CASH,
    LOAD_TABLE_FIELDS,
    LOAD_TABLE_FIELDS_ERROR,
    LOAD_TABLE_FIELDS_SUCCESSFUL,
    LOAD_TABLE_FILTER_AND_SORT,
    LOAD_TABLE_FILTER_AND_SORT_ERROR,
    LOAD_TABLE_FILTER_AND_SORT_SUCCESSFUL,
    LOAD_TABLE_LIST,
    LOAD_TABLE_LIST_ERROR,
    LOAD_TABLE_LIST_FULLY,
    LOAD_TABLE_LIST_SUCCESSFUL,
    LOAD_TABLE_META,
    LOAD_TABLE_META_ERROR,
    LOAD_TABLE_META_PARAMETERS_SUCCESSFUL,
    LOAD_TABLE_META_SUCCESSFUL,
    LOAD_TABLE_SUB_ROW,
    LOAD_TABLE_SUB_ROW_ERROR,
    OPEN_BASKET,
    OPEN_CREATE_INQUIRY,
    OPEN_FOOTER_WIZARD_EXPENSES_DETAIL,
    OPEN_LABEL_EDITOR,
    OPEN_MOBILE_CASH,
    OPEN_MOBILE_CHANGE_SERVICE,
    OPEN_MOBILE_CHANGE_TARIFF,
    OPEN_MOBILE_CHANGROLE,
    RESET_BASKET,
    RESET_BASKET_COUNT,
    RESET_BASKET_SUCCESS,
    SEND_FIELD_SUGGEST,
    SEND_FIELD_SUGGEST_FAILED,
    SEND_FIELD_SUGGEST_SUCCESSFUL,
    SEND_TABLE_FIELDS,
    SEND_TABLE_FIELDS_SIZE,
    SEND_TABLE_FILTER_AND_SORT_ERROR,
    SEND_TABLE_FILTER_AND_SORT_SUCCESSFUL,
    SEND_TABLE_SEARCH,
    SEND_TABLE_SEARCH_ERROR,
    SEND_TABLE_SEARCH_SUCCESSFUL,
    SET_BASKET_PARAMS,
    SET_BASKET_VALID,
    SET_RANGE_MONTH_FILTER,
    SET_RANGE_MONTH_FILTER_SUCCESS,
    SET_TABLE_CURRENT_NAME,
    SET_TABLE_FILTER,
    SET_TABLE_FILTER_PRESET,
    SET_TABLE_IS_LOADING,
    SET_TABLE_MAIN_CHECKBOX,
    SET_TABLE_MAIN_CHECKBOX_ERROR,
    SET_TABLE_SORT,
    SET_TABLE_SUB_ROW_DATA,
    SET_TABLE_SUB_ROW_MESSAGE,
    START_TICK,
    STOP_TICK,
    SWITCH_CHECKBOX,
    SWITCH_CHECKBOX_LOADING,
    SWITCH_CHECKBOX_SUCCESSFUL,
    SWITCH_CHECKBOX_ERROR,
    TABLE_BUTTONS_SET_MODE,
    TABLE_MODAL_OPEN_REMOVE,
    TABLE_REMOVE_ROW_ACTION,
    TABLE_REMOVE_ROW_FAILURE,
    TABLE_REMOVE_ROW_SUCCESS,
    TABLE_RESET,
    TABLE_ROW_DELETE_ACTION,
    TABLE_ROW_RESTORE_ACTION,
    TABLE_ROW_RESTORE_ALL_ACTION,
    UPDATE_ROWS_START,
    UPDATE_ROWS_START_FAILURE,
    UPDATE_ROWS_START_SUCCESSFUL,
    FAST_ACTION_SWITCH_CHECKBOX,
    MOBILE_CASH_FILE_LOADING,
    MOBILE_CASH_FILE_SUCCESSFULL,
    SET_BASKET_CONTEXT,
    SET_IS_FAST_ACTION_PAGE,
    SET_IS_FAST_BASKET_PAGE,
} from '../constants/actionConstants';
import type { ITableBasketParams, ITableReducer, ITableState } from '../reducers/tableReducer';
import type { ColumnFieldType } from './columnTypes';

export enum TableNameType {
    account = 'account',
    autoReportIdAccount = 'autoReportIdAccount',
    autoReportIdAccountEdit = 'autoReportIdAccountEdit', // соответствует таблице на списке ЛС
    autoReportIdSubscriber = 'autoReportIdSubscriber',
    autoReportIdSubscriberEdit = 'autoReportIdSubscriberEdit', // соответствует таблице на списке МС
    bills = 'bills',
    campaignInfoAccount = 'campaignInfoAccount',
    campaignInfoSubscriber = 'campaignInfoSubscriber',
    cashbackHistory = 'cashbackHistory',
    expensesAnalyzer = 'expensesAnalyzer',
    importPersonalData = 'importPersonalData',
    importSubscribersLimits = 'importSubscribersLimits',
    labelsImport = 'labelsImport',
    payments = 'payments',
    productSelectSubscribers = 'productSelectSubscribers',
    settingsAutoReports = 'settingsAutoReports',
    settingsStatistics = 'settingsStatistics',
    wizardBillDocAccount = 'wizardBillDocAccount',
}

export enum TableFooterNameType {
    account = 'account',
    accountId = 'accountId',
    bills = 'bills',
    billsUnpaid = 'billsUnpaid',
    billsUnpaidAccountId = 'billsUnpaidAccountId',
    billsAccountId = 'billsAccountId',
    expensesAnalyzer = 'expensesAnalyzer',
    subscriberId = 'subscriberId',
    subscriberMobile = 'subscriber/mobile',
}

export const mapTableFooterToTableName = {
    [TableFooterNameType.account]: TableNameType.account,
    [TableFooterNameType.accountId]: TableNameType.account,
    [TableFooterNameType.bills]: TableNameType.bills,
    [TableFooterNameType.billsUnpaid]: TableNameType.bills,
    [TableFooterNameType.billsUnpaidAccountId]: TableNameType.bills,
    [TableFooterNameType.billsAccountId]: TableNameType.bills,
    [TableFooterNameType.expensesAnalyzer]: TableNameType.expensesAnalyzer,
    [TableFooterNameType.subscriberId]: TableFooterNameType.subscriberMobile,
    [TableFooterNameType.subscriberMobile]: TableFooterNameType.subscriberMobile,
};

export type TableDataType =
    | IBills
    | ICashbackHistoryTableData
    | IExpensesAnalyzerData
    | ILabelsImportTableData
    | IPersonalAccount
    | ISettingsAutoReportsTableData
    | ISettingsStatisticsTableData
    | ISubscriberFixed
    | ISubscriberMobile
    | IWizardProductOfferingsTableData
    | TAutoReportIdTableAccountData
    | TAutoReportIdTableSubscriberData
    | TCampaignInfoTableAccountData
    | TCampaignInfoTableSubscriberData
    | TPagePaymentsTableData;

export type TableFooterType = IBillsTableFooter | IExpensesAnalyzerTableFooter | { total: string } | undefined;

export interface ITableDictionaryItem {
    def: string;
    id: number;
}

export interface ITableTableNamePayload {
    tableName?: TableNameType;
}

export interface IFieldResize {
    id: string;
    value: number;
}

export const TableWithBasket: TableNameType[] = [
    TableNameType.account,
    TableNameType.bills,
    TableNameType.campaignInfoAccount,
    TableNameType.campaignInfoSubscriber,
    TableNameType.expensesAnalyzer,
    TableNameType.wizardBillDocAccount,
];

export interface ITableFooterSize {
    footerHeight: number;
    horScrollHeight: number;
}

/*
 * -------------------------------------------
 *           SET TABLE CURRENT NAME
 * -------------------------------------------
 */

export type TSetTableCurrentNameAction = IActionWithPayload<typeof SET_TABLE_CURRENT_NAME, TableNameType>;

/*
 * -------------------------------------------
 *           LOAD TABLE META
 * -------------------------------------------
 */

export interface ILoadTableMetaPayload {
    force?: boolean;
    id?: number;
    tableName: TableNameType;
    skipLoadList?: boolean;
    waitFilterAndSort?: boolean;
    waitResetBasket?: boolean;
    queryParams?: Record<string, string>;
}

export type TLoadTableMetaAction = IActionWithPayload<typeof LOAD_TABLE_META, ILoadTableMetaPayload>;

export interface ILoadTableMetaSuccessfulPayload {
    buttons: ITableReducer[TableNameType]['buttons'];
    exportExtensions: string[];
    fields: ITableReducer[TableNameType]['fields'];
    filter: ITableReducer[TableNameType]['filter'];
    filterOptions: ITableReducer[TableNameType]['filterOptions'];
    metaFacets: ITableReducer[TableNameType]['metaFacets'];
    parameters: ITableReducer[TableNameType]['parameters'];
    sort: ITableReducer[TableNameType]['sort'];
    tableName: TableNameType;
}

export type TLoadTableMetaSuccessfulAction = IActionWithPayload<
    typeof LOAD_TABLE_META_SUCCESSFUL,
    ILoadTableMetaSuccessfulPayload
>;

export type TLoadTableMetaParametersSuccessfulAction = IActionWithPayload<
    typeof LOAD_TABLE_META_PARAMETERS_SUCCESSFUL,
    ITableReducer[TableNameType]['parameters']
>;

export interface ILoadTableMetaErrorPayload {
    error: string;
    tableName: TableNameType;
}

export type TLoadTableMetaErrorAction = IActionWithPayload<typeof LOAD_TABLE_META_ERROR, ILoadTableMetaErrorPayload>;

/*
 * -------------------------------------------
 *           LOAD TABLE LIST
 * -------------------------------------------
 */

export interface ILoadTableListPayload {
    force?: boolean;
}

export type TLoadTableListFullyAction = IAction<typeof LOAD_TABLE_LIST_FULLY>;

export type TLoadTableListAction = IActionWithOptionalPayload<typeof LOAD_TABLE_LIST, ILoadTableListPayload>;

export interface ILoadTableListSuccessfulPayload
    extends Pick<ITableReducer[TableNameType], 'tableFooter' | 'data' | 'count' | 'basketAllIn' | 'fastActionAllIn' | 'searchAfterValues'>,
        Partial<Pick<ILoadTableMetaSuccessfulPayload, 'metaFacets'>> {
    tableName: TableNameType;
}

export type TLoadTableListSuccessfulAction = IActionWithPayload<
    typeof LOAD_TABLE_LIST_SUCCESSFUL,
    ILoadTableListSuccessfulPayload
>;

export interface ILoadTableListErrorPayload {
    error: string;
    tableName: TableNameType;
}

export type TLoadTableListErrorAction = IActionWithPayload<typeof LOAD_TABLE_LIST_ERROR, ILoadTableListErrorPayload>;

export interface IResetBasketCountActionPayload {
    tableName: TableNameType;
}

export type TResetBasketCountAction = IActionWithOptionalPayload<
    typeof RESET_BASKET_COUNT,
    IResetBasketCountActionPayload
>;

/*
 * -------------------------------------------
 *           TABLE SUB ROWS
 * -------------------------------------------
 */

export interface IAddTableSubRowIdPayload {
    subRowId: number;
}

export type TAddTableSubRowIdAction = IActionWithPayload<typeof ADD_TABLE_SUB_ROW_ID, IAddTableSubRowIdPayload>;

export interface ILoadTableSubRowPayload {
    subRowId: number;
}

export type TLoadTableSubRowAction = IActionWithPayload<typeof LOAD_TABLE_SUB_ROW, ILoadTableSubRowPayload>;

export type TCollapseTableSubRows = IActionWithPayload<typeof COLLAPSE_TABLE_SUB_ROWS, boolean>;

export interface ILoadTableSubRowErrorPayload {
    error: string;
    subRowId: number;
}

export type TLoadTableSubRowErrorAction = IActionWithPayload<
    typeof LOAD_TABLE_SUB_ROW_ERROR,
    ILoadTableSubRowErrorPayload
>;

export interface ISetTableSubRowDataPayload {
    data: ITableReducer[TableNameType]['data'];
}

export type TSetTableSubRowDataAction = IActionWithPayload<typeof SET_TABLE_SUB_ROW_DATA, ISetTableSubRowDataPayload>;

export interface ISetTableSubRowMessagePayload {
    subRowId: number;
    message: string;
}

export type TSetTableSubRowMessageAction = IActionWithPayload<
    typeof SET_TABLE_SUB_ROW_MESSAGE,
    ISetTableSubRowMessagePayload
>;

/*
 * -------------------------------------------
 *         LOAD TABLE FIELDS
 * -------------------------------------------
 */
export type TLoadTableFieldsAction = IActionWithPayload<typeof LOAD_TABLE_FIELDS, ILoadTableListPayload>;

export interface ILoadTableFieldsSuccessfulPayload {
    fields: ITableReducer[TableNameType]['fields'];
}

export type TLoadTableFieldsSuccessfulAction = IActionWithPayload<
    typeof LOAD_TABLE_FIELDS_SUCCESSFUL,
    ILoadTableFieldsSuccessfulPayload
>;

export interface ILoadTableFieldsErrorPayload {
    error: string;
}

export type TLoadTableFieldsErrorAction = IActionWithPayload<
    typeof LOAD_TABLE_FIELDS_ERROR,
    ILoadTableFieldsErrorPayload
>;

/*
 * -------------------------------------------
 *         SEND TABLE FIELDS
 * -------------------------------------------
 */
export interface ISendTableFieldsPayload {
    fields: ITableReducer[TableNameType]['fields'];
    tableName: TableNameType;
}

export type TSendTableFieldsAction = IActionWithPayload<typeof SEND_TABLE_FIELDS, ISendTableFieldsPayload>;

export interface ISendTableFieldsSizePayload {
    fields: Array<{ name: string; size: number }>;
    tableName: TableNameType;
}

export type TSendTableFieldsSizeAction = IActionWithPayload<typeof SEND_TABLE_FIELDS_SIZE, ISendTableFieldsSizePayload>;

/*
 * -------------------------------------------
 *         LOAD TABLE FILTER AND SORT
 * -------------------------------------------
 */

export type TLoadTableFilterAndSortAction = IActionWithPayload<
    typeof LOAD_TABLE_FILTER_AND_SORT,
    ITableTableNamePayload
>;

export interface ILoadTableFilterAndSortSuccessfulPayload extends ITableTableNamePayload {
    filter: ITableReducer[TableNameType]['filter'];
    sort: IFilterSort;
}

export type TLoadTableFilterAndSortSuccessfulAction = IActionWithPayload<
    typeof LOAD_TABLE_FILTER_AND_SORT_SUCCESSFUL,
    ILoadTableFilterAndSortSuccessfulPayload
>;

export interface ILoadTableFilterAndSortErrorPayload extends ITableTableNamePayload {
    error: string;
}

export type TLoadTableFilterAndSortErrorAction = IActionWithPayload<
    typeof LOAD_TABLE_FILTER_AND_SORT_ERROR,
    ILoadTableFilterAndSortErrorPayload
>;

/*
 * -------------------------------------------
 *         SET TABLE FILTER & ORDER
 * -------------------------------------------
 */

export interface ISetTableFilterPayload extends ITableTableNamePayload {
    currentFilter?: ITableReducer[TableNameType]['filter'];
    filter: ITableReducer[TableNameType]['filter'];
    skipLoadList?: boolean;
}

export type TSetTableFilterAction = IActionWithPayload<typeof SET_TABLE_FILTER, ISetTableFilterPayload>;

export interface ISetTableSortPayload extends ITableTableNamePayload {
    order: ValueOf<ISortEnum>;
    property: string;
    skipLoadList?: boolean;
}

export type TSetTableSortAction = IActionWithPayload<typeof SET_TABLE_SORT, ISetTableSortPayload>;

export const TableFilterPresetQueryId = 'filterId';

export interface ISetTableFilterPresetPayload {
    filterId: string | null;
    tableName: TableNameType;
}

export type TSetTableFilterPresetAction = IActionWithPayload<
    typeof SET_TABLE_FILTER_PRESET,
    ISetTableFilterPresetPayload
>;

/*
 * -------------------------------------------
 *         SET RANGE MONTH PICKER
 * -------------------------------------------
 */
export interface ISetRangeMonthFilterPayload extends ITableTableNamePayload {
    filter: IFilterRangeParams;
    skipLoadList?: boolean;
}

export type TSetRangeMonthFilterAction = IActionWithPayload<typeof SET_RANGE_MONTH_FILTER, ISetRangeMonthFilterPayload>;
export type TSetRangeMonthFilterSuccess = IAction<typeof SET_RANGE_MONTH_FILTER_SUCCESS>;

/*
 * -------------------------------------------
 *         SEND TABLE FILTER & ORDER
 * -------------------------------------------
 */

export type TSendTableFilterAndSortSuccessfulAction = IAction<typeof SEND_TABLE_FILTER_AND_SORT_SUCCESSFUL>;

export interface ISendTableFilterAndSortErrorPayload extends ITableTableNamePayload {
    error: string;
}

export type TSendTableFilterAndSortErrorAction = IActionWithPayload<
    typeof SEND_TABLE_FILTER_AND_SORT_ERROR,
    ISendTableFilterAndSortErrorPayload
>;

/*
 * -------------------------------------------
 *                CHECKBOX
 * -------------------------------------------
 */
export interface ICheckboxItem {
    id: number;
    label: string;
    params?: {
        billPeriod?: string;
        clientId?: number;
        number?: number;
    };
    value: boolean;
}

export interface ISwitchCheckboxPayload extends ITableTableNamePayload {
    id?: number | string;
    isAllChecked?: boolean;
    items?: ICheckboxItem[];
    params?: {
        [param: string]: any;
    };
}

export type TSetIsFastActionPage =
    IActionWithPayload<typeof SET_IS_FAST_ACTION_PAGE, boolean | undefined>;

export type TSetIsFastBasketPage =
    IActionWithPayload<typeof SET_IS_FAST_BASKET_PAGE, boolean | undefined>;

export type TSetBasketContext =
    IActionWithPayload<typeof SET_BASKET_CONTEXT, string | undefined>;

export type TFastActionSwitchCheckboxAction = 
    IActionWithPayload<typeof FAST_ACTION_SWITCH_CHECKBOX, ISwitchCheckboxPayload>;

export type TSwitchCheckboxAction = IActionWithPayload<typeof SWITCH_CHECKBOX, ISwitchCheckboxPayload>;
export type TSwitchCheckboxLoadingAction = IAction<typeof SWITCH_CHECKBOX_LOADING>;
export type TSwitchCheckboxSuccessfulAction = IAction<typeof SWITCH_CHECKBOX_SUCCESSFUL>;

export type TSwitchCheckboxErrorAction = IActionWithPayload<typeof SWITCH_CHECKBOX_ERROR, string>;

export interface ISetTableMainCheckboxPayload extends ITableTableNamePayload {
    basketAllIn: boolean;
}

export type TSetTableMainCheckboxAction = IActionWithPayload<
    typeof SET_TABLE_MAIN_CHECKBOX,
    ISetTableMainCheckboxPayload
>;

export type TSetTableMainCheckboxErrorAction = IActionWithPayload<typeof SET_TABLE_MAIN_CHECKBOX_ERROR, string>;

export type TGetBasketCountAction = IAction<typeof GET_BASKET_COUNT>;

export type TGetBasketCountSuccessfulAction = IActionWithPayload<typeof GET_BASKET_COUNT_SUCCESSFUL, number>;

export type TGetBasketCountFailedAction = IActionWithPayload<typeof GET_BASKET_COUNT_FAILED, string>;

export type TSetBasketValidAction = IActionWithPayload<typeof SET_BASKET_VALID, boolean>;

export type TTableSetBasketParamsAction = IActionWithPayload<typeof SET_BASKET_PARAMS, ITableBasketParams>;

/*
 * -------------------------------------------
 *                DROP FILTER
 * -------------------------------------------
 */

export interface IDropTableFilterAndSortPayload extends ITableTableNamePayload {
    skipLoadList?: boolean;
}

export type TDropTableFilterAndSortAction = IActionWithPayload<
    typeof DROP_TABLE_FILTER_AND_SORT,
    IDropTableFilterAndSortPayload
>;

/*
 * -------------------------------------------
 *                SEND SEARCH
 * -------------------------------------------
 */

export interface ISendTableSearchPayload extends ITableTableNamePayload {
    field: string;
    search: string;
}

export type TSendTableSearchAction = IActionWithPayload<typeof SEND_TABLE_SEARCH, ISendTableSearchPayload>;

export interface ISendTableSearchSuccessfulPayload extends ITableTableNamePayload {
    result: string[];
}

export type TSendTableSearchSuccessfulAction = IActionWithPayload<
    typeof SEND_TABLE_SEARCH_SUCCESSFUL,
    ISendTableSearchSuccessfulPayload
>;

export interface ISendTableSearchErrorPayload extends ITableTableNamePayload {
    error: string;
}

export type TSendTableSearchErrorAction = IActionWithPayload<
    typeof SEND_TABLE_SEARCH_ERROR,
    ISendTableSearchErrorPayload
>;

/*
 * -------------------------------------------
 *                SEND SUGGEST
 * -------------------------------------------
 */
export interface ISendFieldSuggestPayload {
    field: keyof ColumnFieldType;
    value: string;
}

export type TSendFieldSuggestAction = IActionWithPayload<typeof SEND_FIELD_SUGGEST, ISendFieldSuggestPayload>;

export type TSendFieldSuggestSuccessfulAction = IActionWithPayload<
    typeof SEND_FIELD_SUGGEST_SUCCESSFUL,
    ITableReducer[TableNameType]['filterOptions']
>;

export type TSendFieldSuggestFailedAction = IActionWithPayload<typeof SEND_FIELD_SUGGEST_FAILED, string>;

/*
 * -------------------------------------------
 *                FOOTER MODAL
 * -------------------------------------------
 */
export interface IChangeTableModalValuePayload {
    mobileCash?: {
        cashValue: string;
    };
}

export type TChangeTableModalValueAction = IActionWithPayload<
    typeof CHANGE_TABLE_MODAL_VALUE,
    IChangeTableModalValuePayload
>;

export interface IOpenCreateInquiryPayload {
    type: ETopicType;
    userId: string;
}

export type TTableFooterPayload = Partial<Pick<IRouteParams, 'clientId'>>;

export type TOpenCreateInquiryAction = IActionWithPayload<typeof OPEN_CREATE_INQUIRY, IOpenCreateInquiryPayload>;

export type TOpenLabelEditorAction = IActionWithPayload<typeof OPEN_LABEL_EDITOR, TLabelEditorUpdatePayload>;

export type TOpenMobileChangeroleAction = IAction<typeof OPEN_MOBILE_CHANGROLE>;

export type TOpenMobileChangeTariffAction = IActionWithPayload<typeof OPEN_MOBILE_CHANGE_TARIFF, TTableFooterPayload>;

export type TOpenMobileChangeServiceAction = IActionWithPayload<typeof OPEN_MOBILE_CHANGE_SERVICE, TTableFooterPayload>;

export type TOpenFooterWizardExpensesDetailAction = IAction<typeof OPEN_FOOTER_WIZARD_EXPENSES_DETAIL>;

export type TOpenMobileCashAction = IActionWithPayload<typeof OPEN_MOBILE_CASH, IOpenMobileCashPayload>;

export type TLoadMobileCashAction = IActionWithPayload<typeof LOAD_MOBILE_CASH, ILoadMobileCashPayload>;

export type TMobileCashFileLoading = IActionWithPayload<typeof MOBILE_CASH_FILE_LOADING, boolean>;
export type TMobileCashFileLoadingSuccesful = IActionWithPayload<typeof MOBILE_CASH_FILE_SUCCESSFULL, boolean>;

export interface IOpenBasketActionPayload {
    gtmProps?: IBasketGTMProps;
    title: string;
}

export type TOpenBasketAction = IActionWithPayload<typeof OPEN_BASKET, IOpenBasketActionPayload>;

export interface IOpenMobileCashPayload {
    clientId?: string;
    onApplyCallback?: (prepaySum: string) => void;
    onCloseCallback?: (type: EGTMWidgetPersonalAccountCloseType) => void;
    onErrorLoadCallback?: (error: string) => void;
    onSuccessLoadCallBack?: (prepaySum: string) => void;
}

export interface ILoadMobileCashPayload {
    amount: string;
    clientId?: string;
    onErrorCallback?: (error: string) => void;
    onSuccessCallBack?: (prepaySum: string) => void;
}

export interface IUpdateRowsStartPayload {
    method: string;
    url: string;
}

export type TUpdateRowsStartAction = IActionWithPayload<typeof UPDATE_ROWS_START, IUpdateRowsStartPayload>;
export type TUpdateRowsStartSuccessfulAction = IAction<typeof UPDATE_ROWS_START_SUCCESSFUL>;
export type TUpdateRowsStartFailureAction = IActionWithPayload<typeof UPDATE_ROWS_START_FAILURE, string>;

/*
 * -------------------------------------------
 *            FOOTER TICK
 * -------------------------------------------
 */

export type TStartTickAction = IAction<typeof START_TICK>;
export type TStopTickAction = IAction<typeof STOP_TICK>;

/*
 * -------------------------------------------
 *        PROXY
 * -------------------------------------------
 */
export enum EResetBasketType {
    CLEAR_FILTER = 'CLEAR_FILTER',
    CLEAR_META = 'CLEAR_META',
}

export interface IResetBasketActionPayload {
    id?: string | number;
    resetBasket?: boolean;
    resetFilter?: boolean;
    resetState?: boolean;
    tableName: TableNameType;
    type: EResetBasketType;
    waitFilterAndSort?: boolean;
    context?: EFastBasketSwitchboxContext;
}

export type TResetBasketAction = IActionWithPayload<typeof RESET_BASKET, IResetBasketActionPayload>;
export type TResetBasketSuccess = IAction<typeof RESET_BASKET_SUCCESS>;

/*
 * -------------------------------------------
 *        SET TABLE IS LOADING
 * -------------------------------------------
 */

export interface ISetTableIsLoadingPayload {
    tableName?: TableNameType;
    isLoading: boolean;
}

export type TSetTableIsLoadingAction = IActionWithPayload<typeof SET_TABLE_IS_LOADING, ISetTableIsLoadingPayload>;

/*
 * -------------------------------------------
 *        REMOVE ROW
 * -------------------------------------------
 */

export interface ITableRemoveRowActionPayload {
    tableName?: TableNameType;
    rowId: number;
}

export type TTableRemoveRowAction = IActionWithPayload<typeof TABLE_REMOVE_ROW_ACTION, ITableRemoveRowActionPayload>;

export interface ITableRemoveRowSuccessPayload {
    tableName?: TableNameType;
    removeId: number;
    selectRowIdCallback: (row: any) => number;
}

export type TTableRemoveRowSuccessAction = IActionWithPayload<
    typeof TABLE_REMOVE_ROW_SUCCESS,
    ITableRemoveRowSuccessPayload
>;

export interface ITableRemoveRowFailurePayload {
    errorMessage: string;
}

export type TTableRemoveRowFailureAction = IActionWithPayload<
    typeof TABLE_REMOVE_ROW_FAILURE,
    ITableRemoveRowFailurePayload
>;

export interface ITableModalOpenRemoveActionPayload {
    tableName?: TableNameType;
    rowId: number;
}

export type TTableModalOpenRemoveAction = IActionWithPayload<
    typeof TABLE_MODAL_OPEN_REMOVE,
    ITableModalOpenRemoveActionPayload
>;

/*
 * -------------------------------------------
 *        TABLE RESET
 * -------------------------------------------
 */

export interface ITableResetActionPayload {
    tableName?: TableNameType;
}

export type TTableResetAction = IActionWithPayload<typeof TABLE_RESET, ITableResetActionPayload>;

/*
 * -------------------------------------------
 *        BUTTONS MODE
 * -------------------------------------------
 */

/**
 * @deprecated
 */
export enum ETableButtonsMode {
    disableAllExceptInArray = 'disableAllExceptInArray',
}

export type TTableButtonsSetModePayload = Pick<ITableState<any, any>, 'buttonsMode' | 'buttonsForCheckOnMode'> &
    ITableTableNamePayload;

export type TTableButtonsSetModeAction = IActionWithPayload<typeof TABLE_BUTTONS_SET_MODE, TTableButtonsSetModePayload>;

/*
 * -------------------------------------------
 *        DELETE ROW IN TABLE
 * -------------------------------------------
 */

export interface ITableRowActionPayload {
    tableName?: TableNameType;
    index: number;
}

export type TTableRowDeleteAction = IActionWithPayload<typeof TABLE_ROW_DELETE_ACTION, ITableRowActionPayload>;
export type TTableRowRestoreAction = IActionWithPayload<typeof TABLE_ROW_RESTORE_ACTION, ITableRowActionPayload>;
export type TTableRowRestoreAllAction = IActionWithPayload<typeof TABLE_ROW_RESTORE_ALL_ACTION, ITableTableNamePayload>;
